// CustomerTimePicker.jsx

import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Box, Grid, Typography, Stack, TextField, MenuItem } from '@mui/material';
import dayjs from 'dayjs';

const formatTimePart = (value) => value.toString().padStart(2, '0');

const hoursOptions = Array.from({ length: 12 }, (_, i) => i + 1);
const minutesOptions = Array.from({ length: 60 }, (_, i) => formatTimePart(i));
const ampmOptions = ['AM', 'PM'];

const CustomerTimePicker = ({ tempSelectedDays, daySlot, selectedPlan, dayTimeState, setDayTimeState }) => {
  const containerRef = useRef(null);
  const [validationErrors, setValidationErrors] = useState({});

  const convertTo24Hour = useCallback((hour12, period) => {
    let hour24 = parseInt(hour12, 10);
    if (period === 'PM' && hour24 !== 12) {
      hour24 += 12;
    } else if (period === 'AM' && hour24 === 12) {
      hour24 = 0;
    }
    return hour24;
  }, []);

  const handleCustomTimeChange = useCallback((dayName, type, part, value) => {
    setDayTimeState((prevState) => {
      const currentDayData = prevState[dayName] || { pickup: null, drop: null };
      const currentTimeString = currentDayData[type];
      let currentHour12, currentMinute, currentPeriod;

      if (currentTimeString) {
        const [hourStr, minuteStr] = currentTimeString.split(':');
        const hour24 = parseInt(hourStr, 10);
        currentMinute = minuteStr;
        if (hour24 >= 12) {
          currentPeriod = 'PM';
          currentHour12 = hour24 === 12 ? 12 : hour24 - 12;
        } else {
          currentPeriod = 'AM';
          currentHour12 = hour24 === 0 ? 12 : hour24;
        }
      } else {
        currentHour12 = 12;
        currentMinute = '00';
        currentPeriod = 'AM';
      }

      let newHour12 = currentHour12;
      let newMinute = currentMinute;
      let newPeriod = currentPeriod;

      if (part === 'hour') {
        newHour12 = parseInt(value, 10);
      } else if (part === 'minute') {
        newMinute = value;
      } else if (part === 'period') {
        newPeriod = value;
      }

      const newHour24 = convertTo24Hour(newHour12, newPeriod);
      const newTimeString = `${formatTimePart(newHour24)}:${newMinute}`;

      const updatedState = {
        ...prevState,
        [dayName]: {
          ...currentDayData,
          [type]: newTimeString,
        },
      };

      const newPickupTime = type === 'pickup' ? newTimeString : currentDayData.pickup;
      const newDropTime = type === 'drop' ? newTimeString : currentDayData.drop;

      if (newPickupTime && newDropTime && newPickupTime === newDropTime) {
        setValidationErrors(prevErrors => ({
          ...prevErrors,
          [dayName]: 'Drop time cannot be the same as pickup time.',
        }));
      } else {
        setValidationErrors(prevErrors => {
          const newErrors = { ...prevErrors };
          delete newErrors[dayName];
          return newErrors;
        });
      }

      sessionStorage.setItem("dayTimeState", JSON.stringify(updatedState));
      return updatedState;
    });
  }, [convertTo24Hour, setDayTimeState]);

  const isTimeBeforeMinTime = useCallback((dayName, currentType, testHour12, testMinute, testPeriod) => {
    if (currentType !== 'drop') return false;
    const pickupTime = dayTimeState[dayName]?.pickup;
    if (!pickupTime) return false;
    const [pickupHour24, pickupMinute] = pickupTime.split(':').map(Number);
    const baseDate = dayjs().startOf('day');
    let minAllowedDropTime = baseDate.hour(pickupHour24).minute(pickupMinute).add(1, 'minute');
    let testHour24 = convertTo24Hour(testHour12, testPeriod);
    let testTime = baseDate.hour(testHour24).minute(parseInt(testMinute, 10));
    if (minAllowedDropTime.hour() >= 12 && testTime.hour() < 12) {
      testTime = testTime.add(1, 'day');
    }
    return testTime.isBefore(minAllowedDropTime, 'minute');
  }, [dayTimeState, convertTo24Hour]);

  // useEffect(() => {
  //   if (containerRef.current) {
  //     containerRef.current.scrollIntoView({ behavior: 'auto' });
  //   }
  // }, [tempSelectedDays, dayTimeState]);

  useEffect(() => {
  // This condition prevents the effect from running if tempSelectedDays is not available
  if (!tempSelectedDays?.length) return;

  // Check if dayTimeState is already populated to avoid re-initializing on every render
  if (Object.keys(dayTimeState).length === 0) {
    const newState = {};

    tempSelectedDays.forEach((dayIndex) => {
      const dayName = daySlot[dayIndex];
      newState[dayName] = {
        pickup: "00:00",
        ...(selectedPlan?.tripType === 2 ? { drop: "00:00" } : {}),
      };
    });

    setDayTimeState(newState);

    // Immediately run validation for initial state
    if (selectedPlan?.tripType === 2) {
      const initialErrors = {};
      tempSelectedDays.forEach((dayIndex) => {
        const dayName = daySlot[dayIndex];
        if (newState[dayName]?.pickup === newState[dayName]?.drop) {
          initialErrors[dayName] = 'Drop time cannot be the same as pickup time.';
        }
      });
      setValidationErrors(initialErrors);
    }
  }
}, [tempSelectedDays, daySlot, selectedPlan, dayTimeState, setDayTimeState]);

  return (
    <>
      {tempSelectedDays?.map((dayIndex) => { // Use optional chaining to prevent crash
        const dayName = daySlot[dayIndex];
        const pickupTimeStr = dayTimeState[dayName]?.pickup;
        const dropTimeStr = selectedPlan?.tripType === 2 ? dayTimeState[dayName]?.drop : null;
        const hasError = !!validationErrors[dayName];
        
        let initialPickupHour = 12, initialPickupMinute = "00", initialPickupPeriod = "AM";
        if (pickupTimeStr) {
          const [hour24, minute] = pickupTimeStr.split(':').map(Number);
          initialPickupMinute = formatTimePart(minute);
          if (hour24 >= 12) {
            initialPickupPeriod = "PM";
            initialPickupHour = hour24 === 12 ? 12 : hour24 - 12;
          } else {
            initialPickupPeriod = "AM";
            initialPickupHour = hour24 === 0 ? 12 : hour24;
          }
        }
        
        let initialDropHour = 12, initialDropMinute = "00", initialDropPeriod = "AM";
        if (dropTimeStr) {
          const [hour24, minute] = dropTimeStr.split(':').map(Number);
          initialDropMinute = formatTimePart(minute);
          if (hour24 >= 12) {
            initialDropPeriod = "PM";
            initialDropHour = hour24 === 12 ? 12 : hour24 - 12;
          } else {
            initialDropPeriod = "AM";
            initialDropHour = hour24 === 0 ? 12 : hour24;
          }
        }

        return (
          <Box key={dayName} sx={{ flexGrow: 1, marginTop: "20px" }}>
            <Grid container spacing={1} alignItems="flex-start">
              <Grid item xs={4}>
                <Typography sx={{ fontSize: "14px", pt: '10px' }}>{dayName}</Typography>
              </Grid>
              <Grid item xs={8}>
                <Grid container spacing={2} direction="column">
                  <Grid item>
                    <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>One Way Pickup Time:</Typography>
                    <Stack direction="row" spacing={1}>
                      <TextField select label="Hour" value={initialPickupHour} onChange={(e) => handleCustomTimeChange(dayName, 'pickup', 'hour', e.target.value)} sx={{ minWidth: 70 }} size="small">
                        {hoursOptions.map((h) => (<MenuItem key={h} value={h}>{h}</MenuItem>))}
                      </TextField>
                      <TextField select label="Minute" value={initialPickupMinute} onChange={(e) => handleCustomTimeChange(dayName, 'pickup', 'minute', e.target.value)} sx={{ minWidth: 70 }} size="small">
                        {minutesOptions.map((m) => (<MenuItem key={m} value={m}>{m}</MenuItem>))}
                      </TextField>
                      <TextField select label="AM/PM" value={initialPickupPeriod} onChange={(e) => handleCustomTimeChange(dayName, 'pickup', 'period', e.target.value)} sx={{ minWidth: 70 }} size="small">
                        {ampmOptions.map((p) => (<MenuItem key={p} value={p}>{p}</MenuItem>))}
                      </TextField>
                    </Stack>
                    {!pickupTimeStr && (<Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block', opacity: pickupTimeStr ? 0 : 1, height: pickupTimeStr ? '0px' : 'auto', overflow: 'hidden', transition: 'opacity 0.2s ease-in-out, height 0.2s ease-in-out', pointerEvents: pickupTimeStr ? 'none' : 'auto' }}>required</Typography>)}
                  </Grid>

                  {selectedPlan?.tripType === 2 && (
                    <Grid item>
                      <Typography variant="caption" display="block" sx={{ mb: 0.5, mt: 1 }}>Two Way Pickup Time:</Typography>
                      <Stack direction="row" spacing={1}>
                        <TextField select label="Hour" value={initialDropHour} onChange={(e) => handleCustomTimeChange(dayName, 'drop', 'hour', e.target.value)} sx={{ minWidth: 70 }} size="small" error={hasError}>
                          {hoursOptions.map((h) => {
                            const isDisabled = isTimeBeforeMinTime(dayName, 'drop', h, initialDropMinute, initialDropPeriod);
                            return (<MenuItem key={h} value={h} disabled={isDisabled}>{h}</MenuItem>);
                          })}
                        </TextField>
                        <TextField select label="Minute" value={initialDropMinute} onChange={(e) => handleCustomTimeChange(dayName, 'drop', 'minute', e.target.value)} sx={{ minWidth: 70 }} size="small" error={hasError}>
                          {minutesOptions.map((m) => {
                            const isDisabled = isTimeBeforeMinTime(dayName, 'drop', initialDropHour, m, initialDropPeriod);
                            return (<MenuItem key={m} value={m} disabled={isDisabled}>{m}</MenuItem>);
                          })}
                        </TextField>
                        <TextField select label="AM/PM" value={initialDropPeriod} onChange={(e) => handleCustomTimeChange(dayName, 'drop', 'period', e.target.value)} sx={{ minWidth: 70 }} size="small" error={hasError}>
                          {ampmOptions.map((p) => {
                            const isDisabled = isTimeBeforeMinTime(dayName, 'drop', initialDropHour, initialDropMinute, p);
                            return (<MenuItem key={p} value={p} disabled={isDisabled}>{p}</MenuItem>);
                          })}
                        </TextField>
                      </Stack>
                      {hasError && (<Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block' }}>{validationErrors[dayName]}</Typography>)}
                      {!dropTimeStr && (<Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block', opacity: dropTimeStr ? 0 : 1, height: dropTimeStr ? '0px' : 'auto', overflow: 'hidden', transition: 'opacity 0.2s ease-in-out, height 0.2s ease-in-out', pointerEvents: dropTimeStr ? 'none' : 'auto' }}>required</Typography>)}
                      {!hasError && !dropTimeStr && (<Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block', opacity: dropTimeStr ? 0 : 1, height: dropTimeStr ? '0px' : 'auto', overflow: 'hidden', transition: 'opacity 0.2s ease-in-out, height 0.2s ease-in-out', pointerEvents: dropTimeStr ? 'none' : 'auto' }}>TwoWay Time must be greater than One Way Pickup Time</Typography>)}
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Box>
        );
      })}
    </>
  );
};

export default CustomerTimePicker;